import PropertyPanel from '../widgets/PropertyPanel.js';
import TexturePicker from '../widgets/TexturePicker.js';
import { ANIMATED_PRESETS } from '../../materials/AnimatedPresets.js';

/**
 * TexturePropertyPanel - UI for texture material properties
 */
export default class TexturePropertyPanel extends PropertyPanel {
  /**
   * Render the texture property panel
   * @param {Object} initialData - Initial property values
   */
  render(initialData = {}) {
    this.clear();

    // URL input subsection
    const urlSection = this.builder.createAnimatedSubsection('Texture URL', 1, false);
    const urlInput = this.builder.createInput(initialData.url || '', 'text');
    urlInput.placeholder = 'Enter texture URL or data URL...';
    urlInput.className = 'gui-input gui-input-full';
    urlInput.addEventListener('input', () => {
      if (this.onChange) this.onChange();
    });
    urlSection.body.appendChild(this.builder.createRow('URL', urlInput, true));
    this.container.appendChild(urlSection.subsection);
    this.controls.urlInput = urlInput;

    // Texture Library subsection with TexturePicker
    const librarySection = this.builder.createAnimatedSubsection('Texture Library', 1, false);
    librarySection.subsection.classList.add('texture-library-subsection');

    const pickerContainer = document.createElement('div');
    this.texturePicker = new TexturePicker({
      onSelect: (texture) => {
        urlInput.value = texture.path;
        if (this.onChange) this.onChange();
      },
      selectedPath: initialData.url || null
    });

    this.texturePicker.loadManifest().then(() => {
      this.texturePicker.render(pickerContainer);
    });

    librarySection.body.appendChild(pickerContainer);
    this.container.appendChild(librarySection.subsection);

    // Animated Textures preset section
    const animSection = this.builder.createAnimatedSubsection('Animated Textures', 1, false);
    const presetSelector = this.builder.createPresetSelector(
      ANIMATED_PRESETS.map(p => ({ value: p.id, label: p.label, thumbnail: p.thumbnail })),
      (preset) => {
        const full = ANIMATED_PRESETS.find(p => p.id === preset.value);
        if (full && this.onAnimatedPreset) this.onAnimatedPreset(full);
      }
    );
    animSection.body.appendChild(presetSelector.container);
    this.container.appendChild(animSection.subsection);

    // Scale controls subsection
    const scaleSection = this.builder.createAnimatedSubsection('Scale', 1, false);

    const xyzControl = this.builder.createXYZControl(
      (axis, value, isLinked) => {
        if (this.onChange) this.onChange();
        if (this.onScaleChange) this.onScaleChange(axis, value, isLinked);
      },
      (linked) => {
        if (this.onChange) this.onChange();
        if (this.onLinkToggle) this.onLinkToggle(linked);
      }
    );

    // Set initial values if provided
    if (initialData.scaleX !== undefined) {
      xyzControl.controls.x.slider.value = initialData.scaleX;
      xyzControl.controls.x.valueInput.value = initialData.scaleX;
    }
    if (initialData.scaleY !== undefined) {
      xyzControl.controls.y.slider.value = initialData.scaleY;
      xyzControl.controls.y.valueInput.value = initialData.scaleY;
    }
    if (initialData.scaleZ !== undefined) {
      xyzControl.controls.z.slider.value = initialData.scaleZ;
      xyzControl.controls.z.valueInput.value = initialData.scaleZ;
    }

    scaleSection.body.appendChild(xyzControl.control);
    this.container.appendChild(scaleSection.subsection);
    this.controls.scaleControl = xyzControl;

    // Offset X control
    const offsetXSlider = this.addSlider('Offset X', -500, 500, 1, initialData.offsetX || 0, (val) => {
      if (this.onChange) this.onChange();
    });
    this.controls.offsetXSlider = offsetXSlider;

    // Offset Y control
    const offsetYSlider = this.addSlider('Offset Y', -500, 500, 1, initialData.offsetY || 0, (val) => {
      if (this.onChange) this.onChange();
    });
    this.controls.offsetYSlider = offsetYSlider;

    // Offset Z control
    const offsetZSlider = this.addSlider('Offset Z', -500, 500, 1, initialData.offsetZ || 0, (val) => {
      if (this.onChange) this.onChange();
    });
    this.controls.offsetZSlider = offsetZSlider;

    // Opacity slider
    const opacitySlider = this.addSlider(
      'Opacity',
      0,
      1,
      0.01,
      initialData.opacity !== undefined ? initialData.opacity : 1,
      (val) => {
        if (this.onOpacityChange) this.onOpacityChange(val);
        if (this.onChange) this.onChange();
      }
    );
    this.controls.opacitySlider = opacitySlider;

    // Blend mode select (advanced)
    const blendSection = this.builder.createAnimatedSubsection('Blend Mode', 1, true);

    const blendSelect = this.builder.createSelect([
      { value: 'normal', label: 'Normal' },
      { value: 'multiply', label: 'Multiply' },
      { value: 'screen', label: 'Screen' },
      { value: 'overlay', label: 'Overlay' },
      { value: 'darken', label: 'Darken' },
      { value: 'lighten', label: 'Lighten' }
    ], initialData.blendMode || 'normal');
    blendSelect.addEventListener('change', () => {
      if (this.onChange) this.onChange();
    });

    blendSection.body.appendChild(this.builder.createRow('Mode', blendSelect));
    this.container.appendChild(blendSection.subsection);
    this.controls.blendSelect = blendSelect;
  }

  /**
   * Get current property values
   * @returns {Object} Current values
   */
  getData() {
    return {
      type: 'texture',
      url: this.controls.urlInput.value,
      scaleX: parseFloat(this.controls.scaleControl.controls.x.slider.value),
      scaleY: parseFloat(this.controls.scaleControl.controls.y.slider.value),
      scaleZ: parseFloat(this.controls.scaleControl.controls.z.slider.value),
      offsetX: parseFloat(this.controls.offsetXSlider.slider.value),
      offsetY: parseFloat(this.controls.offsetYSlider.slider.value),
      offsetZ: parseFloat(this.controls.offsetZSlider.slider.value),
      opacity: parseFloat(this.controls.opacitySlider.slider.value),
      blendMode: this.controls.blendSelect.value
    };
  }

  /**
   * Set property values
   * @param {Object} data - Property values
   */
  setData(data) {
    if (data.url) {
      this.controls.urlInput.value = data.url;
      // Update texture picker selection if it matches
      if (this.texturePicker) {
        this.texturePicker.setSelection(data.url);
      }
    }
    if (data.scaleX !== undefined) {
      this.controls.scaleControl.controls.x.slider.value = data.scaleX;
      this.controls.scaleControl.controls.x.valueInput.value = data.scaleX;
    }
    if (data.scaleY !== undefined) {
      this.controls.scaleControl.controls.y.slider.value = data.scaleY;
      this.controls.scaleControl.controls.y.valueInput.value = data.scaleY;
    }
    if (data.scaleZ !== undefined) {
      this.controls.scaleControl.controls.z.slider.value = data.scaleZ;
      this.controls.scaleControl.controls.z.valueInput.value = data.scaleZ;
    }
    if (data.offsetX !== undefined) {
      this.controls.offsetXSlider.slider.value = data.offsetX;
      this.controls.offsetXSlider.valueInput.value = data.offsetX;
    }
    if (data.offsetY !== undefined) {
      this.controls.offsetYSlider.slider.value = data.offsetY;
      this.controls.offsetYSlider.valueInput.value = data.offsetY;
    }
    if (data.offsetZ !== undefined) {
      this.controls.offsetZSlider.slider.value = data.offsetZ;
      this.controls.offsetZSlider.valueInput.value = data.offsetZ;
    }
    if (data.opacity !== undefined) {
      this.controls.opacitySlider.slider.value = data.opacity;
      this.controls.opacitySlider.valueInput.value = data.opacity;
    }
    if (data.blendMode) {
      this.controls.blendSelect.value = data.blendMode;
    }
  }

  /**
   * Clean up resources
   */
  clear() {
    if (this.texturePicker) {
      this.texturePicker.destroy();
      this.texturePicker = null;
    }
    super.clear();
  }
}
